import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { HardwareConfigService } from '../hardware/hardware-config.service';
import { VisitService } from '../visit/visit.service';

const INACTIVITY_DEFAULT_SECONDS = 30;
const INACTIVITY_EVENTS = ['pointerdown', 'touchstart', 'keydown', 'wheel'] as const;
const HOME_PATH = '/';
const DOCUMENT_VIEWER_PREFIX = '/documents/';

@Injectable({ providedIn: 'root' })
export class InactivityService {
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly visitService = inject(VisitService);
  private readonly hardwareConfig = inject(HardwareConfigService);

  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private detachListeners: (() => void) | null = null;
  private currentUrl = HOME_PATH;
  private started = false;

  start(): void {
    if (this.started) return;
    this.started = true;

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        this.currentUrl = this.normalizeUrl(e.urlAfterRedirects);
        this.applyForCurrentRoute();
      });

    this.attachInteractionListeners();
    this.applyForCurrentRoute();

    this.destroyRef.onDestroy(() => this.stop());
  }

  private applyForCurrentRoute(): void {
    if (this.shouldWatch(this.currentUrl)) {
      this.resetIdleTimer();
    } else {
      this.clearIdleTimer();
    }
  }

  private shouldWatch(url: string): boolean {
    if (url.startsWith(DOCUMENT_VIEWER_PREFIX)) return false;
    return true;
  }

  private idleTimeoutMs(): number {
    const raw = this.hardwareConfig.settings()?.['ui_config.borne.inactivity_timeout'];
    const seconds = Number(raw);
    const valid = Number.isFinite(seconds) && seconds > 0 ? seconds : INACTIVITY_DEFAULT_SECONDS;
    return valid * 1000;
  }

  private attachInteractionListeners(): void {
    const onInteraction = () => {
      if (this.shouldWatch(this.currentUrl)) {
        this.resetIdleTimer();
      }
    };

    this.zone.runOutsideAngular(() => {
      for (const ev of INACTIVITY_EVENTS) {
        document.addEventListener(ev, onInteraction, { passive: true, capture: true });
      }
    });

    this.detachListeners = () => {
      for (const ev of INACTIVITY_EVENTS) {
        document.removeEventListener(ev, onInteraction, { capture: true });
      }
    };
  }

  private resetIdleTimer(): void {
    this.clearIdleTimer();
    this.zone.runOutsideAngular(() => {
      this.idleTimer = setTimeout(() => {
        this.zone.run(() => this.onIdleTimeout());
      }, this.idleTimeoutMs());
    });
  }

  private clearIdleTimer(): void {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private async onIdleTimeout(): Promise<void> {
    this.clearIdleTimer();
    if (this.visitService.current()) {
      await this.visitService.endVisit('Auto');
    }
    // Already on the accueil (a lingering visit was just closed there): no redirect,
    // the timer simply stops until the next interaction re-arms it.
    if (this.currentUrl !== HOME_PATH) {
      await this.router.navigate([HOME_PATH]);
    }
  }

  private stop(): void {
    this.clearIdleTimer();
    if (this.detachListeners) {
      this.detachListeners();
      this.detachListeners = null;
    }
    this.started = false;
  }

  private normalizeUrl(url: string): string {
    const noQuery = url.split('?')[0].split('#')[0];
    if (noQuery.length > 1 && noQuery.endsWith('/')) {
      return noQuery.slice(0, -1);
    }
    return noQuery || HOME_PATH;
  }
}
